/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { config } from '@/payment-services/api';
import { UIProvider } from '@adobe-commerce/elsie/components';
import { Lang } from '@adobe-commerce/elsie/i18n';
import { deepmerge } from '@adobe-commerce/elsie/lib/deepmerge';
import { events } from '@adobe-commerce/event-bus';
import { FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import en_US from '../i18n/en_US.json';

// Langs
const langDefinitions = {
  default: en_US,
};

interface CartProviderProps {
  children?: any;
}

export const Provider: FunctionComponent<CartProviderProps> = ({
  children,
}) => {
  const [locale, setLang] = useState<Lang>();

  const userLangDefinitions = config.getConfig()?.langDefinitions;

  //   Events
  useEffect(() => {
    const localeEvent = events.on(
      'locale',
      (payload) => {
        if (payload !== locale) setLang(payload as Lang);
      },
      { eager: true }
    );
    return () => {
      localeEvent?.off();
    };
  }, [locale]);

  // Merge language definitions with user language definitions
  const definitions = deepmerge(langDefinitions, userLangDefinitions ?? {});

  return (
    <UIProvider lang={locale} langDefinitions={definitions}>
      {children}
    </UIProvider>
  );
};
